// Nutrient estimator: computes personalized nutrient targets from user profile.
// Each nutrient has a dedicated estimation function.
// See docs/NUTRIENTS_*.md for research backing these values.

import type { UserProfile, EstimatedTargets, NutrientAlgorithm } from '../types.js'

// ===== Helpers =====

/** Mifflin-St Jeor BMR (kcal/day) */
function bmr(p: UserProfile): number {
  const base = 10 * p.weightKg + 6.25 * p.heightCm - 5 * p.age
  return p.sex === 'male' ? base + 5 : base - 161
}

/** Physical Activity Level multiplier */
function pal(p: UserProfile): number {
  switch (p.activityLevel) {
    case 'sedentary': return 1.2
    case 'light': return 1.375
    case 'moderate': return 1.55
    case 'active': return 1.725
    case 'veryActive': return 1.9
  }
}

/** Total Daily Energy Expenditure */
function tdee(p: UserProfile): number {
  let base = bmr(p) * pal(p)
  if (p.pregnant === 'trimester2') base += 340
  if (p.pregnant === 'trimester3') base += 452
  if (p.lactating) base += 400
  return base
}

/** Round nutrient values for clean display:
 *  0-10: round to 0.1, 10-100: round to 1, 100+: round to nearest 5 */
function round(v: number | null): number | null {
  if (v === null) return null
  if (v < 10) return Math.round(v * 10) / 10
  if (v < 100) return Math.round(v)
  return Math.round(v / 5) * 5
}

function ignore(): EstimatedTargets {
  return { min: null, recommended: null, ul: null, ld50: null, algorithm: 'ignore' }
}

function targets(
  min: number | null,
  recommended: number | null,
  ul: number | null,
  ld50: number | null,
  algorithm: NutrientAlgorithm
): EstimatedTargets {
  return { min: round(min), recommended: round(recommended), ul: round(ul), ld50: round(ld50), algorithm }
}

// ===== Per-nutrient estimators =====
// Keyed by FDC Nutrient ID.

type Estimator = (p: UserProfile, energy: number) => EstimatedTargets

const estimators: Record<number, Estimator> = {

  // ── BASICS ──

  // Energy (kcal)
  1008: (p, energy) => {
    const min = bmr(p) // never eat below BMR
    return targets(min, energy, energy * 1.5, null, 'reachMinDontExceedMax')
  },

  // Protein (g)
  1003: (p) => {
    let multiplier = 0.8 // RDA minimum
    switch (p.activityLevel) {
      case 'sedentary': multiplier = 1.0; break
      case 'light': multiplier = 1.2; break
      case 'moderate': multiplier = 1.4; break
      case 'active': multiplier = 1.8; break
      case 'veryActive': multiplier = 2.0; break
    }
    if (p.pregnant || p.lactating) multiplier = Math.max(multiplier, 1.1)
    const rec = multiplier * p.weightKg
    const min = 0.8 * p.weightKg
    const ul = 3.5 * p.weightKg
    return targets(min, rec, ul, null, 'reachMinDontExceedMax')
  },

  // Total lipid / fat (g)
  1004: (_p, energy) => {
    const min = energy * 0.20 / 9
    const rec = energy * 0.30 / 9
    const ul = energy * 0.40 / 9
    return targets(min, rec, ul, null, 'reachMinDontExceedMax')
  },

  // Carbohydrate, by difference (g)
  1005: (p, energy) => {
    let min = 130 // brain glucose requirement
    if (p.pregnant) min = 175
    if (p.lactating) min = 210
    const rec = energy * 0.50 / 4
    const ul = energy * 0.65 / 4
    return targets(min, rec, ul, null, 'reachMinDontExceedMax')
  },

  // Water (g)
  1051: () => {
    // Water from food only — informational
    return targets(500, 800, null, null, 'ignore')
  },

  // Fiber (g)
  1079: (p, energy) => {
    let rec: number
    if (p.sex === 'male') rec = 38
    else rec = 25
    if (p.pregnant) rec = 28
    if (p.lactating) rec = 29
    // Also consider energy-based: 14g per 1000 kcal
    const energyBased = 14 * (energy / 1000)
    rec = Math.max(rec, energyBased)
    const min = rec * 0.7
    return targets(min, rec, 70, null, 'reachMinDontExceedMax')
  },

  // ── SUGARS ──

  // Total sugars (g) — don't exceed
  2000: (_p, energy) => {
    const ul = energy * 0.10 / 4 // WHO 10% guideline
    return targets(null, null, ul, null, 'dontExceedMax')
  },

  // ── VITAMINS ──

  // Vitamin D (IU)
  1110: (p) => {
    let rec = 600
    if (p.age > 70) rec = 800
    // Increase for higher BMI
    const bmi = p.weightKg / ((p.heightCm / 100) ** 2)
    if (bmi > 30) rec = Math.min(rec * 2, 2000)
    return targets(rec, rec, 4000, null, 'reachMinDontExceedMax')
  },

  // Vitamin C (mg)
  1162: (p) => {
    let rec = p.sex === 'male' ? 90 : 75
    if (p.smoker) rec += 35
    if (p.pregnant) rec = 85
    if (p.lactating) rec = 120
    return targets(10, rec, 2000, null, 'reachMinDontExceedMax')
  },

  // Folate, total (µg)
  1177: (p) => {
    let rec = 400
    if (p.pregnant) rec = 600
    if (p.lactating) rec = 500
    return targets(200, rec, 1000, null, 'reachMinDontExceedMax')
  },

  // Vitamin B-12 (µg)
  1178: (p) => {
    let rec = 2.4
    if (p.pregnant) rec = 2.6
    if (p.lactating) rec = 2.8
    return targets(rec, rec, null, null, 'reachMin')
  },

  // Choline (mg)
  1180: (p) => {
    let rec = p.sex === 'male' ? 550 : 425
    if (p.pregnant) rec = 450
    if (p.lactating) rec = 550
    return targets(300, rec, 3500, 272000, 'reachMinDontExceedMax')
  },

  // Vitamin A, RAE (µg)
  1106: (p) => {
    let rec = p.sex === 'male' ? 900 : 700
    if (p.pregnant) rec = 770
    if (p.lactating) rec = 1300
    return targets(300, rec, 3000, 485000, 'reachMinDontExceedMax')
  },

  // Vitamin E (mg)
  1109: (p) => {
    let rec = 15
    if (p.lactating) rec = 19
    return targets(5, rec, 1000, 160000, 'reachMinDontExceedMax')
  },

  // Vitamin B-6 (mg)
  1175: (p) => {
    let rec = 1.3
    if (p.age > 50) rec = p.sex === 'male' ? 1.7 : 1.5
    if (p.pregnant) rec = 1.9
    if (p.lactating) rec = 2.0
    return targets(0.5, rec, 100, 320000, 'reachMinDontExceedMax')
  },

  // Vitamin K (phylloquinone) (µg)
  1185: (p) => {
    const rec = p.sex === 'male' ? 120 : 90
    return targets(50, rec, null, null, 'reachMin')
  },

  // Folate, DFE (µg) — same targets as folate total
  1190: (p) => {
    let rec = 400
    if (p.pregnant) rec = 600
    if (p.lactating) rec = 500
    return targets(200, rec, 1000, null, 'reachMinDontExceedMax')
  },

  // ── MINERALS ──

  // Calcium (mg)
  1087: (p) => {
    let rec = 1000
    if (p.sex === 'female' && p.age >= 51) rec = 1200
    if (p.age >= 71) rec = 1200
    const ul = p.age >= 51 ? 2000 : 2500
    return targets(500, rec, ul, 516000, 'reachMinDontExceedMax')
  },

  // Iron (mg)
  1089: (p) => {
    let rec: number
    if (p.sex === 'male' || p.age >= 51) {
      rec = 8
    } else {
      rec = 18 // premenopausal female
    }
    if (p.pregnant) rec = 27
    if (p.lactating) rec = 9
    // Vegetarian/vegan multiplier
    if (p.dietType === 'vegetarian' || p.dietType === 'vegan') {
      rec *= 1.8
    }
    const min = p.sex === 'male' ? 5 : 10
    return targets(min, rec, 45, 8000, 'reachMinDontExceedMax')
  },

  // Magnesium (mg)
  1090: (p) => {
    let rec: number
    if (p.sex === 'male') {
      rec = p.age <= 30 ? 400 : 420
    } else {
      rec = p.age <= 30 ? 310 : 320
    }
    if (p.pregnant) rec = 360
    if (p.lactating) rec = 320
    return targets(200, rec, 800, 400000, 'reachMinDontExceedMax')
  },

  // Potassium (mg)
  1092: (p) => {
    let rec = p.sex === 'male' ? 3400 : 2600
    if (p.pregnant) rec = 2900
    if (p.lactating) rec = 2800
    return targets(1600, rec, null, 130000, 'reachMin')
  },

  // Sodium (mg)
  1093: (p) => {
    let ul = 2300
    if (p.age >= 51) ul = 2300 // CDRR stays at 2300
    return targets(500, 1500, ul, 96000, 'dontExceedMax')
  },

  // Phosphorus (mg)
  1091: (p) => {
    const rec = p.age <= 18 ? 1250 : 700
    const ul = p.age >= 71 ? 3000 : 4000
    return targets(400, rec, ul, null, 'reachMinDontExceedMax')
  },

  // Zinc (mg)
  1095: (p) => {
    let rec = p.sex === 'male' ? 11 : 8
    if (p.pregnant) rec = 11
    if (p.lactating) rec = 12
    if (p.dietType === 'vegetarian' || p.dietType === 'vegan') {
      rec *= 1.5
    }
    return targets(4, rec, 40, 87200, 'reachMinDontExceedMax')
  },

  // Copper (mg)
  1098: (p) => {
    let rec = 0.9
    if (p.pregnant) rec = 1.0
    if (p.lactating) rec = 1.3
    return targets(0.4, rec, 10, 6, 'reachMinDontExceedMax')
  },

  // Fluoride (µg)
  1099: (p) => {
    const rec = p.sex === 'male' ? 4000 : 3000
    return targets(1000, rec, 10000, 1840000, 'reachMinDontExceedMax')
  },

  // Manganese (mg)
  1101: (p) => {
    let rec = p.sex === 'male' ? 2.3 : 1.8
    if (p.pregnant) rec = 2.0
    if (p.lactating) rec = 2.6
    return targets(1.0, rec, 11, 46400, 'reachMinDontExceedMax')
  },

  // Selenium (µg)
  1103: (p) => {
    let rec = 55
    if (p.pregnant) rec = 60
    if (p.lactating) rec = 70
    return targets(20, rec, 400, 560000, 'reachMinDontExceedMax')
  },

  // ── FATTY ACIDS ──

  // Trans fats (g) — don't exceed
  1257: (_p, energy) => {
    const ul = energy * 0.01 / 9
    return targets(null, 0, ul, null, 'dontExceedMax')
  },

  // Saturated fat (g) — don't exceed
  1258: (_p, energy) => {
    const ul = energy * 0.10 / 9
    return targets(null, 0, ul, null, 'dontExceedMax')
  },

  // DHA (g)
  1272: (p) => {
    let rec = 0.25
    if (p.pregnant || p.lactating) rec = 0.3
    return targets(0.1, rec, 2.0, null, 'reachMinDontExceedMax')
  },

  // EPA (g)
  1278: () => {
    return targets(0.05, 0.25, 3.0, null, 'reachMinDontExceedMax')
  },

  // ALA (g)
  1404: (p) => {
    let rec = p.sex === 'male' ? 1.6 : 1.1
    if (p.pregnant) rec = 1.4
    if (p.lactating) rec = 1.3
    return targets(0.5, rec, null, null, 'reachMin')
  },

  // ── SKIP-STATUS VITAMINS (medium priority — provide values where meaningful) ──

  // Thiamin (mg)
  1165: (p) => {
    let rec = p.sex === 'male' ? 1.2 : 1.1
    if (p.pregnant || p.lactating) rec = 1.4
    return targets(0.5, rec, null, null, 'reachMin')
  },

  // Riboflavin (mg)
  1166: (p) => {
    let rec = p.sex === 'male' ? 1.3 : 1.1
    if (p.pregnant) rec = 1.4
    if (p.lactating) rec = 1.6
    return targets(0.5, rec, null, null, 'reachMin')
  },

  // Niacin (mg)
  1167: (p) => {
    let rec = p.sex === 'male' ? 16 : 14
    if (p.pregnant) rec = 18
    if (p.lactating) rec = 17
    return targets(5, rec, 35, 560000, 'reachMinDontExceedMax')
  },

  // Pantothenic acid (mg)
  1170: (p) => {
    let rec = 5
    if (p.pregnant) rec = 6
    if (p.lactating) rec = 7
    return targets(2, rec, null, 800000, 'reachMin')
  },

  // ── AMINO ACIDS (weight-based, WHO/FAO/UNU 2007) ──

  // Tryptophan (g) — 4 mg/kg
  1210: (p) => {
    const min = 0.004 * p.weightKg
    const rec = 0.005 * p.weightKg
    return targets(min, rec, null, 131, 'reachMin')
  },

  // Threonine (g) — 15 mg/kg
  1211: (p) => {
    const min = 0.015 * p.weightKg
    const rec = 0.020 * p.weightKg
    return targets(min, rec, null, 240, 'reachMin')
  },

  // Isoleucine (g) — 20 mg/kg
  1212: (p) => {
    const min = 0.020 * p.weightKg
    const rec = 0.025 * p.weightKg
    return targets(min, rec, null, null, 'reachMin')
  },

  // Leucine (g) — 39 mg/kg
  1213: (p) => {
    let multiplier = 0.039
    if (p.activityLevel === 'active' || p.activityLevel === 'veryActive') multiplier = 0.045
    if (p.age > 65) multiplier = Math.max(multiplier, 0.045) // anabolic resistance
    const min = 0.039 * p.weightKg
    const rec = multiplier * p.weightKg
    return targets(min, rec, null, null, 'reachMin')
  },

  // Lysine (g) — 30 mg/kg
  1214: (p) => {
    const min = 0.030 * p.weightKg
    const rec = 0.038 * p.weightKg
    return targets(min, rec, null, null, 'reachMin')
  },

  // Methionine (g) — 10.4 mg/kg (alone), 15 mg/kg (met+cys)
  1215: (p) => {
    const min = 0.0104 * p.weightKg
    const rec = 0.015 * p.weightKg // met+cys combined target
    return targets(min, rec, null, 272, 'reachMin')
  },

  // Cystine (g) — conditionally essential
  1216: (p) => {
    const rec = 0.0046 * p.weightKg
    return targets(null, rec, null, null, 'ignore')
  },

  // Phenylalanine (g) — 25 mg/kg (phe+tyr combined, ~half for phe alone)
  1217: (p) => {
    const min = 0.0125 * p.weightKg
    const rec = 0.015 * p.weightKg
    return targets(min, rec, null, null, 'reachMin')
  },

  // Tyrosine (g) — conditionally essential
  1218: (p) => {
    const rec = 0.0125 * p.weightKg
    return targets(null, rec, null, null, 'ignore')
  },

  // Valine (g) — 26 mg/kg
  1219: (p) => {
    const min = 0.026 * p.weightKg
    const rec = 0.032 * p.weightKg
    return targets(min, rec, null, null, 'reachMin')
  },

  // Histidine (g) — 10 mg/kg
  1221: (p) => {
    const min = 0.010 * p.weightKg
    const rec = 0.014 * p.weightKg
    return targets(min, rec, null, null, 'reachMin')
  },

  // PUFA 18:2 / Linoleic acid (g) — essential omega-6
  1269: (p, energy) => {
    const rec = p.sex === 'male' ? 17 : 12
    const min = energy * 0.05 / 9
    const ul = energy * 0.10 / 9
    return targets(min, rec, ul, null, 'reachMinDontExceedMax')
  },

  // Cholesterol (mg)
  1253: () => {
    return targets(null, null, 300, null, 'dontExceedMax')
  },

  // Total MUFA (g) — informational
  1292: (_p, energy) => {
    const rec = energy * 0.17 / 9
    return targets(null, rec, null, null, 'ignore')
  },

  // Total PUFA (g) — informational
  1293: (_p, energy) => {
    const min = energy * 0.05 / 9
    const rec = energy * 0.08 / 9
    const ul = energy * 0.10 / 9
    return targets(min, rec, ul, null, 'ignore')
  },
}

// ===== Main entry point =====

export function estimateAll(profile: UserProfile): Record<number, EstimatedTargets> {
  // Compute energy first — many nutrients depend on it
  const energy = tdee(profile)
  const result: Record<number, EstimatedTargets> = {}

  for (const [idStr, estimator] of Object.entries(estimators)) {
    const id = parseInt(idStr)
    result[id] = estimator(profile, energy)
  }

  return result
}

/**
 * Get estimated targets for a single nutrient.
 * Returns ignore() if no estimator is registered.
 */
export function estimateNutrient(nutrientId: number, profile: UserProfile): EstimatedTargets {
  const estimator = estimators[nutrientId]
  if (!estimator) return ignore()
  const energy = tdee(profile)
  return estimator(profile, energy)
}
